import {
    MetricPerformanceData,
    MetricTooltipData,
    TrendData,
} from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { bgGradient } from '../../common/pipe/bg-gradient';
import { sdpNumber, sdpPercent } from '../../common/pipe/single-decimal-point';
import { CardPerformanceDataContainer } from '../../insight/models/card-performance-data-container.model';
import { PerformanceDataViewType } from '../../insight/performance-data/metric.pd.model';
import { epicDependencies } from '../../store';
import { TranslationKeyMap } from '../my-performance-card.i18n';

import { getThresholdData } from './get-threshold-data.function';

export const prepareMetricPerformanceData = (
    input: CardPerformanceDataContainer,
    metricPerformanceData: boolean,
    userIdsHaveOtherManagers?: number[],
): MetricPerformanceData => {
    if (epicDependencies == null || epicDependencies.injector == null) {
        return;
    }
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);
    const calculatedThresholds = getThresholdData(input);
    // Edge case: when actual is greater than max
    if (
        input.minValueMetric > input.maxValueMetric &&
        input.actual >= input.minValueMetric &&
        Array.isArray(calculatedThresholds) &&
        calculatedThresholds.length > 0
    ) {
        const allDisabled = calculatedThresholds.every(
            (threshold) => threshold?.enabled === false,
        );

        if (allDisabled) {
            calculatedThresholds[0].enabled = true;
        }
    }

    // Edge case: when target is null or thresholdBands is empty/undefined
    const hasThresholdBands =
        Array.isArray(input.thresholdBands) && input.thresholdBands.length > 0;
    const hasTarget = input.target != null;

    const color =
        hasThresholdBands && hasTarget
            ? input.targetThresholdColour
            : '#bababa';
    const trendValues = calculateTrendValues(
        input.actual ?? 0,
        input.previousActual ?? 0,
        color,
        translateService,
    );

    const actualLabel: string = translateService.instant(
        TranslationKeyMap._725_ACTUAL,
    );
    const targetLabel: string = translateService.instant(
        TranslationKeyMap._726_TARGET,
    );
    const goalLabel: string = translateService.instant(
        TranslationKeyMap._721_GOAL,
    );
    const superGroupLabel: string = translateService.instant(
        TranslationKeyMap._67_SUPER_GROUP,
    );
    const toggleStateTooltip = {
        expandedTooltipLabel: translateService.instant(
            TranslationKeyMap._10091_EXPAND_ROW,
        ),
        collapsedTooltipLabel: translateService.instant(
            TranslationKeyMap._10092_COLLAPSE_ROW,
        ),
    };

    const actualValueTooltip = prepareMetricTooltipData(
        actualLabel,
        input.actual,
        `${actualLabel}: ${sdpNumber(input.actual ?? 0) ?? ''}`,
        translateService,
    );
    const targetValueTooltip = prepareMetricTooltipData(
        targetLabel,
        input.target,
        `${targetLabel}: ${sdpNumber(input.target ?? 0) ?? ''}`,
        translateService,
    );
    const goalValueTooltip =
        input.goal != null
            ? prepareMetricTooltipData(
                  goalLabel,
                  input.goal,
                  `${goalLabel}: ${sdpNumber(input.goal ?? 0) ?? ''}`,
                  translateService,
              )
            : null;
    const superGroupTooltip = prepareMetricTooltipData(
        superGroupLabel,
        input.overallSuperGroupActual,
        `${superGroupLabel}:  ${
            sdpNumber(input.overallSuperGroupActual ?? 0) ?? ''
        }`,
        translateService,
    );

    let noActualDataMessage;
    let noTargetDataMessage;

    if (input.actual == null) {
        noActualDataMessage = translateService.instant(
            TranslationKeyMap._11172_NO_RESULTS_TO_DISPLAY,
        );
    }

    if (
        input.target == null ||
        input.target === 0 ||
        input.thresholdBands == null
    ) {
        noTargetDataMessage = translateService.instant(
            TranslationKeyMap._11173_NO_TARGET_THRESHOLDS_CONFIGURED,
        );
    }

    const user =
        metricPerformanceData &&
        input.viewType === PerformanceDataViewType.USER_SUPER_GROUP
            ? {
                  userId: input.primaryId,
                  userName: input.primaryName,
                  hasPreviousReports:
                      userIdsHaveOtherManagers?.includes(input.primaryId) ??
                      false,
              }
            : null;

    let queue: { name: string; tooltip: string } = null;
    if (
        metricPerformanceData &&
        input.viewType === PerformanceDataViewType.USER_QUEUE
    ) {
        queue = {
            name: input.secondaryName,
            tooltip: input.secondaryName,
        };
    }

    return {
        metricName: input.metricName,
        metricNameTooltip: input.metricName,
        metricId: input.metricID,
        actual: input.actual,
        rankOrder: input.rankOrder,
        trendValues,
        progressBarData: {
            thresholds: calculatedThresholds,
            actualValue: actualValueTooltip,
            targetValue: targetValueTooltip,
            goalValue: goalValueTooltip,
            superGroupValue: superGroupTooltip,
            minValue: Math.round(input.minValueMetric ?? 0),
            maxValue: Math.round(input.maxValueMetric ?? 100),
        },
        color: input.targetThresholdColour,
        noActualDataMessage,
        noTargetDataMessage,
        isVisible: true,
        isExpandable: false,
        isExpanded: false,
        toggleStateTooltip,
        isChild: metricPerformanceData,
        user,
        queue,
    };
};

const prepareMetricTooltipData = (
    title: string,
    value: number | undefined,
    tooltipText: string,
    translateService: TranslateService,
): MetricTooltipData | undefined => {
    if (value !== undefined) {
        return {
            title,
            value,
            tooltipText: translateService.instant(tooltipText),
        };
    }
    return undefined;
};

const calculateTrendValues = (
    currentActual: number,
    previousActual: number,
    thresholdColor: string,
    translateService: TranslateService,
): TrendData => {
    let trendArrow = '';
    let trendValue = '--';
    let trendColor = '#000';
    const trendViewText = translateService.instant(
        TranslationKeyMap._10075_VIEW_TREND,
    );
    if (previousActual == null || currentActual == null) {
        trendArrow = '';
        trendValue = '--';
    } else {
        const trendDifference =
            (currentActual - previousActual) / previousActual;
        trendValue = sdpPercent(Math.abs(trendDifference));
        if (currentActual > previousActual) {
            trendArrow = 'arrow-up';
            trendColor = '#137e0f';
        } else if (currentActual < previousActual) {
            trendArrow = 'arrow-down';
            trendColor = '#ce2521';
        }
    }

    if (currentActual === previousActual) {
        trendArrow = 'arrow-left-right';
        trendValue = sdpPercent(0);
        trendColor = '#000';
    }

    return {
        actual: sdpNumber(currentActual),
        trendColor,
        thresholdColor,
        thresholdColorGradient: bgGradient({
            color: thresholdColor,
        }),
        trendValue,
        trendArrow,
        trendViewText,
    };
};
